import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CONTACT, MAILTO } from '../data/contactInfo';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const footerRef = useRef(null);
  const navigate = useNavigate();

  // Observer d'entrée pour animations
  useEffect(() => {
    const currentRef = footerRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(currentRef);
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []);

  // Liens de navigation
  const navigationLinks = [
    { name: "Accueil", url: "/" },
    { name: "À Propos", url: "/a-propos" },
    { name: "Réalisations", url: "/realisations" },
    { name: "Contact", url: "/contact" },
    { name: "Plan du Site", url: "/sitemap" }
  ];

  // Services spécialisés
  const servicesLinks = [
    { name: "Portrait", url: "/services/portraits" },
    { name: "Image d'entreprise", url: "/services/entreprise" },
    { name: "Evenement", url: "/services/evenementiel" },
    // Boutique masquée temporairement : { name: "Print", url: "/print" },
  ];

  // Informations légales
  const legalLinks = [
    { name: "Mentions Légales", url: "/mentions-legales" },
  ];

  // Handler CTA avec temps de chargement
  const handleCTA = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      navigate("/contact");
    }, 600);
  };

  return (
    <footer ref={footerRef} className={`footer-section ${isVisible ? 'visible' : ''}`}>
     
      <div className="footer-cta">
        <div className="footer-container">
          <div className="cta-content">
            <h2 className="cta-title">Me contacter pour un projet ?</h2>
            <button
              onClick={handleCTA}
              className={`cta-button ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Parlons-en !'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal du footer */}
      <div className="footer-main">
        <div className="footer-container">
          <div className="footer-grid">
            
            {/* Section Branding + Description */}
            <div className="footer-brand footer-block">
              <div className="brand-logo">
                <h3 className="logo-text">Tanguy Perroit</h3>
                <span className="logo-subtitle">Photographe Professionnel</span>
              </div>
              <p className="brand-description">
                Photographe professionnel spécialisé dans la photographie corporate, 
                les portraits professionnels et le shooting produit. Excellence et créativité 
                au service de votre image depuis 2019.
              </p>
              <div className="social-links">
                <a
                  href={CONTACT.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Instagram"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a 
                  href={CONTACT.social.linkedin}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link"
                  aria-label="LinkedIn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Section Navigation */}
            <div className="footer-section footer-block">
              <h4 className="section-title">Navigation</h4>
              <ul className="section-links">
                {navigationLinks.map((link, index) => (
                  <li key={index}>
                    <Link to={link.url} className="footer-link">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Section Services */}
            <div className="footer-section footer-block">
              <h4 className="section-title">Services</h4>
              <ul className="section-links">
                {servicesLinks.map((link, index) => (
                  <li key={index}>
                    <Link to={link.url} className="footer-link">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Section Contact */}
            <div className="footer-section footer-block">
              <h4 className="section-title">Contact</h4>
              <div className="contact-info">
                <a href={MAILTO} className="contact-link">
                  <svg className="contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {CONTACT.email}
                </a>
                <a href={CONTACT.phone.href} className="contact-link">
                  <svg className="contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  {CONTACT.phone.display}
                </a>
                <div className="contact-link">
                  <svg className="contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {CONTACT.location.display}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer bottom */}
      <div className="footer-bottom">
        <div className="footer-container">
          <div className="bottom-content">
            <div className="copyright">
              <p>&copy; 2026 Tanguy Perroit. Tous droits réservés.</p>
            </div>
            <div className="legal-links">
              {legalLinks.map((link, index) => (
                <Link key={index} to={link.url} className="legal-link">
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
